import { Injectable, inject, signal } from '@angular/core';

import { QuotationProxyService } from '../proxies/order/quotation.proxy.service';
import { QuotationEntry, Quotation } from '../models/order/quotation';
import _ from 'lodash';
import { FormControl, FormGroup, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Business } from '../models/finance/business';
import { ItemMaster } from '../models/item/item-master/item-master';
import { EntityKeyByReference } from '@card-software/nexus-utilities';

export interface Department {
  id: string;
  name: string;
  code: string;
}

// No longer exported from a shared model file; kept here with the same
// shape createQuotationFormGroup already expected.
interface OldQuotation {
  department?: Department | null;
  expirationDate?: string;
  currency?: number;
  exchangeRateToUSD?: number;
  issuedDate?: string;
  paymentMethodCode?: string;
  provider?: Business | null;
  unsavedProviderName?: string | null;
}

export interface QuotationFormGroup {
  department: FormControl<Department | null>;
  expirationDate: FormControl<string>;
  currency: FormControl<number>;
  exchangeRateToUSD: FormControl<number>;
  issuedDate: FormControl<string>;
  paymentMethodCode: FormControl<string>;
  provider: FormControl<Business | null>;
  unsavedProviderName: FormControl<string | null>;
  items: FormArray<FormGroup<ItemEntryFormGroup>>;
}

export interface ItemEntryFormGroup {
  itemCode: FormControl<ItemMaster | null>;
  brand: FormControl<string>;
  unitPrice: FormControl<number>;
  isTaxExempt: FormControl<boolean>;
}

@Injectable({
  providedIn: 'root',
})
export class RequisitionService {
  private readonly quotationProxyService = inject(QuotationProxyService);
  isLoading = signal(0);
  public quotations = signal<Quotation[]>([]);

  // Persists which Requisitions tab (Mine(All) / Mine(Pending Approval) /
  // General Overview / Requisition Reports) is active across
  // RequisitionsComponent destroy/recreate cycles — e.g. navigating to
  // Requisition Details and back via Cancel/Save, which is a real route
  // change and would otherwise reset component-local state to its default.
  public activeTab = signal(4);

  public requisitionEntityKey: EntityKeyByReference = {
    reference: '68cb86f4c90068a80328fa9a',
    entityDefinitionCode: 0,
  };

  // Custom validator for provider fields - at least one must be provided
  private static providerValidator(control: AbstractControl): ValidationErrors | null {
    const formGroup = control as FormGroup;
    const provider = formGroup.get('provider')?.value;
    const unsavedProviderName = formGroup.get('unsavedProviderName')?.value;

    const hasProvider = provider && provider.id;
    const hasUnsavedProvider = unsavedProviderName && unsavedProviderName.trim();

    if (!hasProvider && !hasUnsavedProvider) {
      return { providerRequired: true };
    }

    return null;
  }

  // Create a new quotation form group
  public createQuotationFormGroup(initialData?: OldQuotation): FormGroup<QuotationFormGroup> {
    const formGroup = new FormGroup<QuotationFormGroup>(
      {
        department: new FormControl<Department | null>(initialData?.department || null, {
          validators: [Validators.required],
          nonNullable: true,
        }),
        expirationDate: new FormControl<string>(initialData?.expirationDate || '', {
          validators: [Validators.required],
          nonNullable: true,
        }),
        currency: new FormControl<number>(typeof initialData?.currency === 'number' ? initialData.currency : 0, {
          validators: [Validators.required],
          nonNullable: true,
        }),
        exchangeRateToUSD: new FormControl<number>(initialData?.exchangeRateToUSD || 1, {
          validators: [Validators.required, Validators.min(0)],
          nonNullable: true,
        }),
        issuedDate: new FormControl<string>(initialData?.issuedDate || '', { validators: [Validators.required], nonNullable: true }),
        paymentMethodCode: new FormControl<string>(initialData?.paymentMethodCode || '', {
          validators: [Validators.required],
          nonNullable: true,
        }),
        provider: new FormControl<Business | null>(initialData?.provider || null),
        unsavedProviderName: new FormControl<string | null>(initialData?.unsavedProviderName || null),
        items: new FormArray<FormGroup<ItemEntryFormGroup>>([]),
      },
      { validators: [RequisitionService.providerValidator] },
    );

    return formGroup;
  }

  // Create item management form group (for add/edit item)
  public createItemManagementFormGroup(initialData?: QuotationEntry): FormGroup<ItemEntryFormGroup> {
    return new FormGroup<ItemEntryFormGroup>({
      itemCode: new FormControl<ItemMaster | null>(
        initialData?.itemCode && initialData?.itemDisplayName
          ? ({
              itemCode: initialData.itemCode,
              itemDisplayName: initialData.itemDisplayName,
            } as ItemMaster)
          : null,
        { validators: [Validators.required], nonNullable: true },
      ),
      brand: new FormControl<string>(initialData?.brand || '', { validators: [Validators.required], nonNullable: true }),
      unitPrice: new FormControl<number>(initialData?.unitPrice || 0, {
        validators: [Validators.required, Validators.min(0)],
        nonNullable: true,
      }),
      isTaxExempt: new FormControl<boolean>(initialData?.isTaxExempt || false, { nonNullable: true }),
    });
  }

  // Add new item to quotation form
  public addItemToQuotation(quotationForm: FormGroup<QuotationFormGroup>, itemData?: QuotationEntry): void {
    const itemForm = this.createItemManagementFormGroup(itemData);
    quotationForm.controls.items.push(itemForm);
  }

  // Remove item from quotation form
  public removeItemFromQuotation(quotationForm: FormGroup<QuotationFormGroup>, index: number): void {
    quotationForm.controls.items.removeAt(index);
  }

  public refreshQuotations(): void {
    const command = {
      entityKey: this.requisitionEntityKey,
    };
    this.isLoading.update((value) => value + 1);
    this.quotationProxyService.search(command).subscribe({
      next: (results) => {
        const data = results.data;
        this.isLoading.update((value) => value - 1);
        if (!_.isNil(data)) {
          this.quotations.update(() => data);
        }
      },
      error: () => {
        this.isLoading.update((value) => value - 1);
      },
    });
  }
}
